import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Users, UserCheck, Shield, Edit } from 'lucide-react';
import { MobileOptimizedCard } from '@/components/MobileOptimizedCard';
import { MobileTable } from '@/components/MobileTable';
import { UsersTableSkeleton, DashboardStatSkeleton } from '@/components/ui/skeletons';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AppRole, UserProfile, CurrentUserData, RoleStats } from '@/lib/settingsTypes';

const userEditSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  position: z.string().optional(),
  department: z.string().optional(),
});

type UserEditFormData = z.infer<typeof userEditSchema>;

interface UserManagementTabProps {
  users: UserProfile[];
  usersLoading: boolean;
  roleStats: RoleStats;
  currentUserData: CurrentUserData | undefined;
  currentUserId?: string;
  onEditUserPermissions: (userId: string) => void;
}

export const UserManagementTab = ({
  users,
  usersLoading,
  roleStats,
  currentUserData,
  currentUserId,
  onEditUserPermissions,
}: UserManagementTabProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);

  const userEditForm = useForm<UserEditFormData>({
    resolver: zodResolver(userEditSchema),
  });

  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AppRole }) => {
      const { error } = await supabase
        .from('user_roles')
        .update({ role })
        .eq('user_id', userId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Role Updated", description: "User role has been successfully updated." });
      queryClient.invalidateQueries({ queryKey: ['users-with-roles'] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update role", variant: "destructive" });
    },
  });

  const updateUserProfileMutation = useMutation({
    mutationFn: async (userData: UserEditFormData & { userId: string }) => {
      const { userId, ...profileData } = userData;
      const { error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('user_id', userId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "User Updated", description: "User profile has been successfully updated." });
      queryClient.invalidateQueries({ queryKey: ['users-with-roles'] });
      setEditingUser(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update user profile", variant: "destructive" });
    },
  });

  const canManageUser = (targetUser: UserProfile): boolean => {
    if (!currentUserData) return false;
    if (currentUserData.isSuperAdmin) {
      return !targetUser.user_roles?.[0]?.is_super_admin || targetUser.user_id === currentUserId;
    }
    return targetUser.user_roles?.[0]?.role !== 'admin' && !targetUser.user_roles?.[0]?.is_super_admin;
  };

  const handleEditUser = (user: UserProfile) => {
    setEditingUser(user);
    userEditForm.reset({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      position: user.position || '',
      department: user.department || '',
    });
  };

  const handleSaveUser = (data: UserEditFormData) => {
    if (editingUser) {
      updateUserProfileMutation.mutate({ ...data, userId: editingUser.user_id });
    }
  };

  const renderRoleBadge = (user: UserProfile) => {
    if (user.user_roles?.[0]?.is_super_admin) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1 w-fit shrink-0">
          <Shield className="h-3 w-3" />
          Super Admin
        </Badge>
      );
    }
    return (
      <Badge 
        variant={
          user.user_roles?.[0]?.role === 'admin' ? 'default' : 
          user.user_roles?.[0]?.role === 'manager' ? 'secondary' : 
          'outline'
        }
        className="w-fit shrink-0"
      >
        {(user.user_roles?.[0]?.role || 'employee').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">User Management</h2>

      {/* Role Statistics */}
      {usersLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <DashboardStatSkeleton key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Administrators</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{(roleStats.admin || 0) + (roleStats.super_admin || 0)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Managers</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{roleStats.manager || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Employees</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{roleStats.employee || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users Table */}
      {usersLoading ? (
        <MobileOptimizedCard title="All Users">
          <UsersTableSkeleton />
        </MobileOptimizedCard>
      ) : isMobile ? (
        <MobileTable
          data={users}
          columns={[
            {
              key: 'name',
              label: 'Name',
              render: (_, user: UserProfile) => `${user.first_name} ${user.last_name}`
            },
            { key: 'email', label: 'Email' },
            {
              key: 'role',
              label: 'Role',
              render: (_, user: UserProfile) => renderRoleBadge(user)
            }
          ]}
          actions={(user: UserProfile) => (
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEditUser(user)}
                disabled={!canManageUser(user)}
                className="h-8 text-xs"
              >
                <Edit className="h-3 w-3 mr-1" />
                Edit User
              </Button>
              <Select
                value={user.user_roles?.[0]?.role || 'employee'}
                onValueChange={(value: AppRole) => {
                  updateUserRoleMutation.mutate({ userId: user.user_id, role: value });
                }}
                disabled={!canManageUser(user)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {currentUserData?.isSuperAdmin && (
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEditUserPermissions(user.user_id)}
                disabled={!canManageUser(user) || (currentUserData?.role !== 'admin' && !currentUserData?.isSuperAdmin)}
                className="h-8 text-xs"
              >
                Edit Permissions
              </Button>
            </div>
          )}
          emptyMessage="No users found"
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>All Users</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user: UserProfile) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.first_name} {user.last_name}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{renderRoleBadge(user)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUser(user)}
                          disabled={!canManageUser(user)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Select
                          value={user.user_roles?.[0]?.role || 'employee'}
                          onValueChange={(value: AppRole) => {
                            updateUserRoleMutation.mutate({ userId: user.user_id, role: value });
                          }}
                          disabled={!canManageUser(user)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employee">Employee</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            {currentUserData?.isSuperAdmin && (
                              <SelectItem value="super_admin">Super Admin</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onEditUserPermissions(user.user_id)}
                          disabled={!canManageUser(user) || (currentUserData?.role !== 'admin' && !currentUserData?.isSuperAdmin)}
                        >
                          Edit Permissions
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogDescription>Update user information and details</DialogDescription>
          </DialogHeader>
          <Form {...userEditForm}>
            <form onSubmit={userEditForm.handleSubmit(handleSaveUser)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={userEditForm.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="First name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={userEditForm.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={userEditForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="email@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={userEditForm.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone (Optional)</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="Phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={userEditForm.control}
                  name="position"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Position (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Job title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={userEditForm.control}
                  name="department"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Department (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Department" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateUserProfileMutation.isPending}>
                  {updateUserProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
